import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { putToSignedUrl } from '../api';
import { useWorkstation } from '../auth';
import { PrinterCamera, usePrinterCamera, type Still } from '../components/PrinterCamera';
import { QrCode } from '../components/QrCode';
import { RefusalPanel } from '../components/RefusalPanel';
import { StateBadge } from '../components/StateBadge';
import { Thumbnail } from '../components/Thumbnail';
import { chime, setSoundOn, soundOn } from '../chime';
import { expectPhoto, finished, needsThumbnail, sortSheets, stillExpected, type Expected } from '../jobs';
import { isBusy, WAITING_STATES, type CaptureCamera, type Job as JobRecord, type Sheet } from '../model';
import { captureOptions } from '../printers';
import { renderThumbnail } from '../thumbnail';

const POLL_MS = 2000;

/**
 * One job: the QR the phone scans, and every sheet in it (F2).
 *
 * Polled rather than pushed. The alternative is a WebSocket API and a
 * subscription to keep alive for a screen that one person looks at for a few
 * minutes at a time; two seconds of latency is below what anyone standing at
 * a printer would notice, and polling stops when nothing is working.
 *
 * A sheet that finishes while the page is open chimes, and opens for review
 * when it is the only thing that was in flight (SLD-0006).
 */
export function Job() {
  const { jobId = '' } = useParams();
  const api = useWorkstation();
  const camera = usePrinterCamera();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [job, setJob] = useState<JobRecord | null>(null);
  // The label being edited, or null when it is not.
  const [draft, setDraft] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  // One attempt per page: a design pdf.js cannot draw stays blank, quietly.
  const drewThumbnail = useRef(false);
  const [sheets, setSheets] = useState<Sheet[]>([]);
  const [capture, setCapture] = useState<{ url: string; expiresAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The one refused sheet whose explanation is open, if any.
  const [explained, setExplained] = useState<string | null>(null);
  // The job's printer's camera setting: undefined until it loads, null when
  // unset or unreadable, which shows both panels (see `captureOptions`).
  const [printerCamera, setPrinterCamera] = useState<CaptureCamera | null>();
  // Photos sent from this page that the pre-check has not picked up yet (see
  // `Expected`); state as well as a ref, so polling starts when one is sent.
  const expected = useRef<Expected>({});
  const [expecting, setExpecting] = useState(false);
  // The sheets as last loaded, to tell what finished since.
  const shown = useRef<Sheet[]>([]);
  // Sheets that finished while the page was open and were not opened for
  // review, marked so they can be found in the list.
  const [fresh, setFresh] = useState<string[]>([]);
  const [sound, setSound] = useState(soundOn);

  const load = useCallback(async () => {
    try {
      const [detail, list] = await Promise.all([
        api.getJob(jobId), api.listSheets(jobId),
      ]);
      const done = finished(shown.current, list.sheets, expected.current);
      shown.current = list.sheets;
      expected.current = stillExpected(expected.current, list.sheets, Date.now());
      setExpecting(Object.keys(expected.current).length > 0);
      setJob(detail);
      setSheets(sortSheets(list.sheets));
      if (done.ready.length) chime('ready');
      else if (done.refused.length) chime('refused');
      // Straight to the review when that is the only thing to do next; with
      // anything else still in flight, leaving would interrupt it.
      const quiet = !list.sheets.some((s) => isBusy(s.state))
        && !Object.keys(expected.current).length;
      if (done.ready.length === 1 && quiet) {
        navigate(`/jobs/${jobId}/sheets/${done.ready[0].sheetId}`);
      } else if (done.ready.length || done.refused.length) {
        const ids = [...done.ready, ...done.refused].map((s) => s.sheetId);
        setFresh((f) => [...f.filter((id) => !ids.includes(id)), ...ids]);
      }
      return list.sheets;
    } catch (e) {
      setError((e as Error).message);
      return [];
    }
  }, [jobId, navigate]);

  useEffect(() => { void load(); }, [load]);

  const printerId = job?.printerId;
  useEffect(() => {
    if (!printerId) return;
    api.getPrinter(printerId)
      .then((p) => setPrinterCamera(p.camera))
      .catch(() => setPrinterCamera(null));
  }, [printerId]);

  // A job from before thumbnails existed gets one here, from its own design,
  // the first time its page is opened. The jobs list then has it too.
  useEffect(() => {
    if (!job || !needsThumbnail(job) || drewThumbnail.current) return;
    drewThumbnail.current = true;
    (async () => {
      try {
        const pdf = await fetch(job.artworkUrl!).then((r) => {
          if (!r.ok) throw new Error(`design fetch ${r.status}`);
          return r.blob();
        });
        const png = await renderThumbnail(pdf);
        const thumb = await api.thumbnailUpload(jobId);
        await putToSignedUrl(thumb.url, png, 'image/png');
        await load();
      } catch (e) {
        console.warn('thumbnail not drawn:', (e as Error).message);
      }
    })();
  }, [job, jobId, load]);

  // `?new=1` is how the jobs page lands here right after creating a job. A
  // printer set to its lid camera gets sheet 1 at once, so the next click is
  // the shot (SLD-0006); otherwise the QR code is up, for the phone. Waits for
  // the printer's setting, which decides which.
  const started = useRef(false);
  useEffect(() => {
    if (!job || !params.has('new') || printerCamera === undefined || started.current) return;
    started.current = true;
    setParams({}, { replace: true });
    if (printerCamera === 'usb') {
      if (!sheets.length) void addSheet();
    } else {
      void makeQr();
    }
  }, [job, printerCamera]);

  // Poll only while something is actually in flight, counting a photo just
  // sent. A job whose sheets are all reviewed is a static page and should cost
  // nothing to leave open.
  useEffect(() => {
    if (!expecting && !sheets.some((s) => isBusy(s.state))) return;
    const timer = setInterval(() => { void load(); }, POLL_MS);
    return () => clearInterval(timer);
  }, [sheets, expecting, load]);

  async function addSheet() {
    try {
      await api.createSheet(jobId);
      await load();
    } catch (e) { setError((e as Error).message); }
  }

  async function makeQr() {
    try { setCapture(await api.captureToken(jobId)); }
    catch (e) { setError((e as Error).message); }
  }

  async function sendPhoto(sheetId: string, photo: Still) {
    // Noted at once, so polling starts; `since` is corrected below.
    const before = sheets.find((s) => s.sheetId === sheetId)?.updatedAt ?? '';
    expected.current = expectPhoto(expected.current, sheetId, before, Date.now());
    setExpecting(true);
    setFresh((f) => f.filter((id) => id !== sheetId));
    try {
      const { url, updatedAt } = await api.photoUpload(jobId, sheetId, photo.timing);
      // Asking for the upload changes the sheet too, so wait from that change:
      // the pre-check cannot start until the photo is in.
      const since = updatedAt ?? before;
      expected.current = expectPhoto(expected.current, sheetId, since, Date.now());
      setExpecting(true);
      await putToSignedUrl(url, photo.blob, 'image/jpeg');
      expected.current = expectPhoto(expected.current, sheetId, since, Date.now());
    } catch (e) {
      const { [sheetId]: _, ...rest } = expected.current;
      expected.current = rest;
      setExpecting(Object.keys(rest).length > 0);
      throw e;
    }
    await load();
  }

  function toggleSound() {
    setSoundOn(!sound);
    setSound(!sound);
  }

  const waiting = sheets.filter((s) => WAITING_STATES.includes(s.state));
  // Neither until the setting is known, so a panel does not show and vanish.
  const offer = printerCamera === undefined
    ? { usb: false, phone: false } : captureOptions(printerCamera);

  async function saveName() {
    const name = (draft ?? '').trim();
    if (!name || !job) { setDraft(null); return; }
    if (name === job.name) { setDraft(null); return; }
    setRenaming(true);
    setError(null);
    try {
      setJob(await api.renameJob(jobId, name));
      setDraft(null);
    } catch (e) { setError((e as Error).message); }
    finally { setRenaming(false); }
  }

  if (!job) return <p className="muted">{error ?? 'Loading…'}</p>;

  return (
    <div className="columns">
      <div className="stack main">
        <section className="panel job-head">
          <div className="job-facts">
            {draft === null ? (
              <h2 className="job-name">
                Job: {job.name}
                <button className="icon" title="Rename this job"
                        aria-label="Rename this job"
                        onClick={() => setDraft(job.name)}>✎</button>
              </h2>
            ) : (
              <form className="rename" onSubmit={(e) => { e.preventDefault(); void saveName(); }}>
                <input value={draft} maxLength={60} autoFocus required
                       aria-label="Job name"
                       onChange={(e) => setDraft(e.target.value)}
                       onKeyDown={(e) => { if (e.key === 'Escape') setDraft(null); }} />
                <button className="primary" type="submit" disabled={renaming}>
                  {renaming ? 'Saving…' : 'Save'}
                </button>
                <button className="link" type="button" disabled={renaming}
                        onClick={() => setDraft(null)}>Cancel</button>
              </form>
            )}
            <p className="muted">
              {job.mesh}-mesh · printer {job.printerId}
              {job.artworkUploadedAt
                ? ' · design uploaded'
                : ' · no design uploaded yet'}
            </p>
            <p className="muted">
              Created <time dateTime={job.createdAt}>
                {new Date(job.createdAt).toLocaleString()}
              </time>
            </p>
            {job.artworkUploadedAt && (
              <p className="muted file-line">
                <span className="code" title="The design file as uploaded">
                  {job.filename ?? 'design.pdf'}
                </span>
                {job.artworkUrl && (
                  <a href={job.artworkUrl} download>Download PDF</a>
                )}
              </p>
            )}
          </div>
          {job.artworkUploadedAt && (
            <Thumbnail job={job} className="job-art" />
          )}
        </section>

        <section className="panel">
          <div className="row-between">
            <h3>Sheets</h3>
            <div className="button-row">
              <button className="link" onClick={toggleSound} aria-pressed={sound}
                      title="A chime when a sheet is ready to review or refused">
                Sound {sound ? 'on' : 'off'}
              </button>
              <button className="primary" onClick={() => void addSheet()}
                      disabled={!job.artworkUploadedAt}>
                Add a sheet
              </button>
            </div>
          </div>
          {!job.artworkUploadedAt && (
            <p className="muted">Upload the design before adding sheets.</p>
          )}
          {sheets.length === 0 && <p className="muted">No sheets yet.</p>}
          <ul className="rows">
            {sheets.map((sheet) => (
              <li key={sheet.sheetId}
                  className={fresh.includes(sheet.sheetId) ? 'fresh' : undefined}>
                <span className="sheet-id">{sheet.sheetId}</span>
                <StateBadge state={sheet.state} />
                {sheet.state === 'needs_review' && (
                  <Link className="primary-link"
                        to={`/jobs/${jobId}/sheets/${sheet.sheetId}`}>
                    Review →
                  </Link>
                )}
                {(sheet.state === 'approved' || sheet.state === 'printed') && (
                  <Link to={`/jobs/${jobId}/sheets/${sheet.sheetId}`}>
                    Open
                  </Link>
                )}
                {sheet.refusal && (
                  // The same panel the phone showed, so the workstation can
                  // read why without asking whoever was holding the phone.
                  <button className="link refusal-code"
                          aria-expanded={explained === sheet.sheetId}
                          onClick={() => setExplained(
                            explained === sheet.sheetId ? null : sheet.sheetId)}>
                    {sheet.refusal.code}
                  </button>
                )}
                <time className="row-date" dateTime={sheet.updatedAt}
                      title="Last changed">
                  {new Date(sheet.updatedAt).toLocaleString()}
                </time>
                {sheet.refusal && explained === sheet.sheetId && (
                  <div className="row-detail">
                    <RefusalPanel refusal={sheet.refusal} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
        {error && <p className="error">{error}</p>}
      </div>

      <div className="stack side">
        {offer.usb && (
          <section className="panel">
            <h3>Capture with the printer camera</h3>
            {/* Set to the lid camera: one click per shot, no preview needed. */}
            <PrinterCamera
              camera={camera}
              quick={printerCamera === 'usb'}
              actions={waiting.map((s) => ({ key: s.sheetId, label: `Shoot sheet ${s.sheetId}` }))}
              onPhoto={sendPhoto}>
              <p className="muted">
                {!waiting.length
                  ? 'No sheets are waiting for a photo.'
                  : printerCamera === 'usb'
                    ? 'The USB camera on the printer lid. Open the lid fully with the sheet on the bed, then shoot: the camera turns on just for the photo. Use the live preview to check the bed first.'
                    : 'The USB camera on the printer lid. Open the lid fully with the sheet on the bed, then shoot.'}
              </p>
            </PrinterCamera>
          </section>
        )}

        {offer.phone && (
          <section className="panel">
            <h3>{offer.usb ? 'Or capture on the phone' : 'Capture on the phone'}</h3>
            {capture ? (
              <div className="qr-block">
                <QrCode url={capture.url} />
                <p className="muted">
                  Scan with the phone at the printer. The link works for this job
                  only and expires{' '}
                  {new Date(capture.expiresAt).toLocaleString()}.
                </p>
              </div>
            ) : (
              <>
                <p className="muted">
                  The phone does not sign in. It gets a link that works for this
                  job and nothing else, for one work session.
                </p>
                <button className="primary" onClick={() => void makeQr()}>
                  Show QR code
                </button>
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
